import type {CellAddress} from '@github-ui/board-core'

import {withArrivingCard} from './board-document-adapter'
import type {SavedViewsBoardItem} from './types'

/** The buckets a confirmed move put a card in. An axis left out says nothing about the card. */
export type MovedCardBuckets = {columnKeys?: readonly string[]; rowKeys?: readonly string[]}

/**
 * The buckets a confirmed move put a card in, on both axes, from the cells the write read the
 * card back in. The write reports only the fields it changed, so its fields alone would lose
 * where an earlier move put the card on the other axis.
 */
export function movedCardBuckets(cells: readonly CellAddress[], {hasRows}: {hasRows: boolean}): MovedCardBuckets {
  const columnKeys = [...new Set(cells.map(cell => cell.columnKey))]
  return hasRows ? {columnKeys, rowKeys: [...new Set(cells.map(cell => cell.rowKey))]} : {columnKeys}
}

/**
 * A reloaded cell's cards, with the cards this board moved between cells kept where the board
 * has them.
 *
 * A cell page comes from the search index, which can lag this board's own writes: a card moved
 * in can be missing from the page, and a card moved out can still be on it. Only membership is
 * kept. The page decides the order and which cards are ranked, because putting a position back
 * over an index that caught up would undo other people's changes.
 *
 * A card put back because the page did not have it arrives the way a moved card does, so a
 * ranked one is marked `positionGuessed`. A card the page places carries no mark. Other cells
 * keep their own marks, so a card drawn in several cells stays guessed where it still is.
 */
export function keepMovedCards<TItem extends SavedViewsBoardItem>({
  page,
  held,
  cell,
  moved,
}: {
  /** The cards the page holds, in order. */
  page: readonly TItem[]
  /** The cards the cell held before the reload. */
  held: readonly TItem[]
  /** The cell the page is for. */
  cell: CellAddress
  /** The cards this board moved between cells, and where each move put them. */
  moved: ReadonlyMap<string, MovedCardBuckets>
}): TItem[] {
  const movedIn = held.filter(card => moved.has(card.globalId))
  const kept = new Set(movedIn.map(card => card.globalId))
  // `held` has only loaded cards, so a moved card it lacks can still be on a page nobody read.
  let items = page.filter(card => kept.has(card.globalId) || !hasLeft(moved.get(card.globalId), cell))

  const placed = new Set(items.map(card => card.globalId))
  const missing = movedIn.filter(card => !placed.has(card.globalId))
  // Reversed, because each ranked card arrives at the top and would otherwise land above the
  // one that was above it.
  for (const card of [...missing].reverse()) items = withArrivingCard(items, card)

  return items
}

function hasLeft(buckets: MovedCardBuckets | undefined, cell: CellAddress): boolean {
  if (buckets === undefined) return false
  const leftColumn = buckets.columnKeys !== undefined && !buckets.columnKeys.includes(cell.columnKey)
  const leftRow = buckets.rowKeys !== undefined && !buckets.rowKeys.includes(cell.rowKey)
  return leftColumn || leftRow
}
